import React, { useEffect, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { Alarm as AlarmIcon } from "@mui/icons-material";
import FormDialog from "../FormDialog";
import { FormCheckbox, TrTimePicker, ActionButton } from "../../atoms";
import { COLORS } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { useSuggestMedicationPlansMutation, useCreateMedicationPlansMutation } from "../../../store/medicationPlanApi";
import { formToCreateBody, planToForm, validateForm } from "./medicationPlanForm";

/**
 * Reçetedeki ilaçlardan alarm planı önerisi. Klinik saatleri düzeltip seçtiklerini
 * oluşturur; zaten planlı ilaçlar varsayılan olarak seçili gelmez.
 */
export default function PrescriptionAlarmSuggestDialog({ open, onClose, patientId, treatmentId, token, onCreated }) {
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();
  const [suggest] = useSuggestMedicationPlansMutation();
  const [create, { isLoading }] = useCreateMedicationPlansMutation();
  const [rows, setRows] = useState([]); // { s, checked, form, newTime }

  useEffect(() => {
    if (!open) return;
    suggest({ token, patientId, treatmentId })
      .unwrap()
      .then((r) =>
        setRows((r?.data?.suggestions || []).map((s) => ({ s, checked: !s.alreadyPlanned, form: planToForm(s), newTime: "" })))
      )
      .catch((e) => showError(e?.data?.message || "Öneri alınamadı"));
  }, [open, token, patientId, treatmentId, suggest, showError]);

  const patchRow = (i, p) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const chosen = rows.filter((r) => r.checked);
  const invalid = chosen.some((r) => Object.keys(validateForm(r.form)).length > 0);

  const submit = async () => {
    try {
      await create({
        token,
        patientId,
        plans: chosen.map((r) => ({
          ...formToCreateBody(r.form),
          treatment: r.s.treatment || treatmentId,
          treatmentMedication: r.s.treatmentMedication,
          medicine: r.s.medicine || undefined,
        })),
      }).unwrap();
      showSuccess(`${chosen.length} alarm planı oluşturuldu`);
      onCreated?.();
      onClose();
    } catch (e) {
      showError(e?.data?.message || "Planlar oluşturulamadı");
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      icon={<AlarmIcon />}
      title="Reçeteden alarm planı"
      subtitle="Saatleri kontrol edip onaylayın"
      maxWidth="md"
      loading={isLoading}
      submitLabel={`Oluştur (${chosen.length})`}
      onSubmit={submit}
      submitDisabled={!chosen.length || invalid}
    >
      {rows.map((r, i) => (
        <Box key={r.s.treatmentMedication || i} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${COLORS.slate200}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <FormCheckbox
              palette={palette}
              checked={r.checked}
              onChange={(v) => patchRow(i, { checked: v })}
              label={r.s.name}
              inputProps={{ "aria-label": r.s.name }}
            />
            <Typography sx={{ fontSize: "0.8rem", color: COLORS.slate500 }}>
              {r.form.dose} {r.form.doseUnit}
            </Typography>
            {r.s.alreadyPlanned ? <Chip size="small" label="Zaten planlı" sx={{ height: 20, fontSize: "0.65rem" }} /> : null}
          </Box>
          {r.checked ? (
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap", mt: 1, pl: 4 }}>
              {r.form.times.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  onDelete={() => patchRow(i, { form: { ...r.form, times: r.form.times.filter((x) => x !== t) } })}
                />
              ))}
              <TrTimePicker
                value={r.newTime}
                onChange={(v) => patchRow(i, { newTime: v })}
                startTime="00:00"
                endTime="23:45"
                label="Saat"
              />
              <ActionButton
                variant="add"
                compact
                label="Ekle"
                palette={palette}
                disabled={!r.newTime}
                onClick={() =>
                  patchRow(i, { newTime: "", form: { ...r.form, times: [...new Set([...r.form.times, r.newTime])].sort() } })
                }
              />
              {!r.form.times.length ? (
                <Typography sx={{ fontSize: "0.75rem", color: COLORS.errorDark }}>Bu ilaç için saat ekleyin</Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ))}
    </FormDialog>
  );
}
